/**
 * The employee's own payslips.
 *
 * Only approved runs appear here — the API enforces that, and this page says
 * so rather than presenting an empty list as though no pay exists. A figure
 * still being corrected must not reach the person it pays.
 *
 * Every line shows the arithmetic behind it. A deduction someone cannot see
 * derived is a deduction they cannot challenge.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { client } from '../api/client';
import { useAuth } from '../store/auth';
import { money } from '../lib/money';
import { PayslipDrawer } from './admin/PayrollPage';
import type { Page, PayslipSummary } from '../types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The period the payslip pays — never the date it happened to be generated. */
function periodOf(slip: PayslipSummary): string {
  return `${MONTHS[slip.month - 1]} ${slip.year}`;
}

export default function PayslipsPage() {
  // Null for a login that was never onboarded, which is ordinary for an
  // administrator and changes what the empty state should say.
  const isEmployee = useAuth((state) => state.user?.employeeId) != null;
  const [openId, setOpenId] = useState<string | null>(null);

  const payslips = useQuery<Page<PayslipSummary>>({
    queryKey: ['my-payslips'],
    queryFn: async () =>
      (await client.get('/api/v1/employees/me/payslips', { params: { limit: 24 } })).data,
  });

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">My Payslips</h2>

        {payslips.isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : payslips.isError ? (
          <div className="card border border-red-200 bg-red-50">
            <p className="text-sm text-red-800">
              Could not load your payslips. This is a failure to fetch, not an
              empty record.
            </p>
            <button
              type="button"
              className="btn-ghost mt-2"
              onClick={() => void payslips.refetch()}
            >
              Retry
            </button>
          </div>
        ) : payslips.data && payslips.data.items.length === 0 ? (
          <div className="card text-center">
            <FileText className="mx-auto h-6 w-6 text-gray-300" />
            {isEmployee ? (
              <>
                <p className="mt-2 text-sm text-gray-600">No payslips yet.</p>
                <p className="mt-1 text-xs text-gray-500">
                  A payslip appears here once that month's payroll has been
                  approved.
                </p>
              </>
            ) : (
              /* Saying "once payroll is approved" to somebody who has no
                 employee record promises something that will never arrive. */
              <>
                <p className="mt-2 text-sm text-gray-600">
                  Your account is not linked to an employee record.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Payslips belong to employees. Administrator accounts are often
                  created without one — if you should be paid through this
                  system, ask an administrator to onboard you.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-medium">Period</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Days Paid</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Gross</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Deductions</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Net Pay</th>
                  <th scope="col" className="px-4 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payslips.data?.items.map((slip) => (
                  <tr key={slip.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {periodOf(slip)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {slip.payableDays}/{slip.totalDays}
                      {Number(slip.lopDays) > 0 && (
                        <span className="ml-1 text-amber-700">
                          ({slip.lopDays} LOP)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {money(slip.grossEarnings)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {money(slip.totalDeductions)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums font-medium text-gray-900">
                      {money(slip.netPay)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setOpenId(slip.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openId && (
        <PayslipDrawer
          payslipId={openId}
          selfService
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
